import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormHelperText } from '@mui/material';
import { FormOfAddress, uploadableImageMediaTypes } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    FormikPoolDatePicker,
    PoolAvatar,
    PoolDropzone,
    PoolTypography,
    ScrollToFieldError,
} from '@synergy/components';
import {
    PaloConfirmationDialog,
    PaloField,
    PaloFormGrid,
    PaloFormSection,
    PaloModal,
    PaloReadValue,
} from '@synergy/components-palo';
import { PaloThemeProvider } from '@synergy/theme-palo';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { forwardRef, useEffect, useRef, useState } from 'react';
import urljoin from 'url-join';

import * as ContactDetailOverviewActions from '../contact-detail/overview/store/contact-detail-overview-actions';
import { ContactDetailOverviewBasicInfos } from '../contact-detail/overview/components/contact-detail-overview-basic-infos';
import { ContactDetailOverviewFormValues } from '../contact-detail/overview/models/contact-detail-overview-form-values';
import { PaloDirtyComparison } from '../../../../common/components/palo/palo-dirty-context';
import { PaloTextControl } from '../../../../common/components/palo/palo-text-control';
import {
    contactDetailOverviewBaselineSelector,
    contactDetailOverviewFormDataSelector,
    contactSelector,
} from '../contact-detail/overview/store/contact-detail-overview-selectors';
import { contactDetailOverviewValidationSchema } from '../contact-detail/overview/contact-detail-overview-validation-schema';
import { contactIsLockedSelector } from '../contact-detail/store/contact-detail-global-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const ContactTopInformation = (): JSX.Element => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const contact = useAppSelector(contactSelector);
    const contactIsLocked = useAppSelector(contactIsLockedSelector);
    const apiBaseUrl = useAppSelector(state => state.settings.apiBaseUrl);
    const imageBaseUrl = useAppSelector(state => state.settings.imageCachePaths?.['original']);
    const relatedAccounts = useAppSelector(
        state => state.contactsAndOrganizations.contacts.contactDetail.overview.relatedAccounts
    );
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

    useEffect(() => {
        if (contact?.id) {
            dispatch(ContactDetailOverviewActions.fetchRelatedAccounts());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact?.id]);

    const handleProfileImageUpload =
        (form: FormikProps<ContactDetailOverviewFormValues>) =>
        (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
            if (rejectedFiles.length > 0) {
                form.setFieldError(
                    'image',
                    rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType
                        ? 'app.error.invalidFileType'
                        : 'app.error.maxOneFile'
                );

                return;
            }
            dispatch(ContactDetailOverviewActions.uploadProfileImage(acceptedFiles[0]));
            setIsUploadModalVisible(false);
        };

    const profileBaseSrc = apiBaseUrl && imageBaseUrl && urljoin(apiBaseUrl, imageBaseUrl);

    return (
        <PaloFormSection title={<FormattedMessage id="app.contactsAndOrganizations.contact" />}>
            <Stack direction="row" gap={3} alignItems="flex-start">
                <Stack sx={{ width: 96, flexShrink: 0 }}>
                    <Field name="image">
                        {({ field, form }: FieldProps<string>) => (
                            <PoolAvatar
                                firstName={
                                    form.values.firstName ||
                                    form.values.lastName ||
                                    intl.formatMessage({ id: 'app.contactsAndOrganizations.contact.new' })
                                }
                                lastName={form.values.firstName ? form.values.lastName : undefined}
                                onUploadClick={() => setIsUploadModalVisible(true)}
                                addLabel={intl.formatMessage({ id: 'app.contactsAndOrganizations.detail.photoAdd' })}
                                removeLabel={intl.formatMessage({
                                    id: 'app.contactsAndOrganizations.detail.photoRemove',
                                })}
                                onDeleteClick={() => setIsDeleteModalVisible(true)}
                                style={{ width: 96, height: 96, fontSize: 34, flexShrink: 0 }}
                                disabled={!contact?.id}
                                src={field.value && profileBaseSrc && urljoin(profileBaseSrc, field.value)}
                                styleType="cover"
                                locked={contactIsLocked}
                            />
                        )}
                    </Field>
                    {!contact?.id && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                        >
                            <FormattedMessage id="app.contactsAndOrganizations.detail.avatarSaveFirst" />
                        </Typography>
                    )}
                </Stack>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <PaloFormGrid>
                        <PaloTextControl
                            name="firstName"
                            label={<FormattedMessage id="app.label.firstName" />}
                            required
                        />
                        <PaloTextControl
                            name="lastName"
                            label={<FormattedMessage id="app.label.lastName" />}
                            required
                        />
                        <Field name="formOfAddress">
                            {({ field, form, meta }: FieldProps<FormOfAddress>) => (
                                <PaloField
                                    label={
                                        <>
                                            <FormattedMessage id="app.label.formOfAddress" /> *
                                        </>
                                    }
                                    name={field.name}
                                    error={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : undefined
                                    }
                                >
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        value={field.value ?? ''}
                                        onChange={event => form.setFieldValue(field.name, event.target.value)}
                                    >
                                        <MenuItem value={FormOfAddress.Mr}>
                                            <FormattedMessage id="app.contactsAndOrganizations.contact.mr" />
                                        </MenuItem>
                                        <MenuItem value={FormOfAddress.Mrs}>
                                            <FormattedMessage id="app.contactsAndOrganizations.contact.mrs" />
                                        </MenuItem>
                                        <MenuItem value={FormOfAddress.Neutral}>
                                            <FormattedMessage id="app.contactsAndOrganizations.contact.neutralName" />
                                        </MenuItem>
                                    </TextField>
                                </PaloField>
                            )}
                        </Field>
                        <PaloTextControl name="title" label={<FormattedMessage id="app.label.title" />} />
                        <PaloField label={<FormattedMessage id="app.label.dateOfBirth" />}>
                            <FormikPoolDatePicker name="dateOfBirth" label={undefined} />
                        </PaloField>
                        <Field name="mainAccount.id">
                            {({ field, form }: FieldProps<number>) => (
                                <PaloField label={<FormattedMessage id="app.label.mainAccount" />}>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        value={field.value ?? ''}
                                        disabled={relatedAccounts.length === 0}
                                        onChange={event => form.setFieldValue(field.name, event.target.value)}
                                    >
                                        {relatedAccounts.map(account => (
                                            <MenuItem key={account.id} value={account.id}>
                                                {account.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </PaloField>
                            )}
                        </Field>
                        <Field name="mainAccountPosition">
                            {({ field }: FieldProps<string>) => (
                                <PaloField label={<FormattedMessage id="app.label.position" />}>
                                    <PaloReadValue>
                                        {/* `??` lets an empty string through, and the field
                                            then stood as a label over nothing. */}
                                        {field.value || (
                                            <FormattedMessage id="app.contactsAndOrganizations.contact.noPosition" />
                                        )}
                                    </PaloReadValue>
                                </PaloField>
                            )}
                        </Field>
                    </PaloFormGrid>
                </Box>
            </Stack>

            <PaloModal
                isVisible={isUploadModalVisible}
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.uploadModal" />}
                handleClose={() => setIsUploadModalVisible(false)}
            >
                <Field name="image">
                    {({ form, meta }: FieldProps<string>) => (
                        <>
                            <PoolTypography
                                typography="body2"
                                id="app.contactsAndOrganizations.detail.validFileTypes"
                            />
                            <PoolDropzone
                                onDrop={handleProfileImageUpload(form)}
                                accept={uploadableImageMediaTypes}
                                multiple={true}
                                maxFiles={1}
                                useFsAccessApi={false}
                            />
                            {meta.error && (
                                <FormHelperText error={true}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </>
                    )}
                </Field>
            </PaloModal>
            <PaloConfirmationDialog
                isVisible={isDeleteModalVisible}
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteAvatar" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setIsDeleteModalVisible(false)}
                onConfirm={() => {
                    dispatch(ContactDetailOverviewActions.removeProfileImage());
                    setIsDeleteModalVisible(false);
                }}
            >
                <FormattedMessage id="app.contactsAndOrganizations.detail.deleteAvatarMessage" />
            </PaloConfirmationDialog>
        </PaloFormSection>
    );
};

export const PaloContactDetailOverview = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const dispatch = useThunkDispatch();
    const formikRef = useRef<FormikProps<ContactDetailOverviewFormValues>>();
    const { formValues, errors, touched } = useAppSelector(contactDetailOverviewFormDataSelector);

    // The contact as the server has it — see PaloDirtyComparison.
    const baseline = useAppSelector(contactDetailOverviewBaselineSelector);
    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(ContactDetailOverviewActions.updateContactDetail(values, isDirty, isValid, errs));
    });

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            initialValues={formValues}
            validationSchema={contactDetailOverviewValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formik => (formikRef.current = formik || undefined)}
        >
            {() => (
                <>
                    <PaloDirtyComparison baseline={baseline} />
                    <PaloThemeProvider>
                        <ContactTopInformation />
                    </PaloThemeProvider>
                    <ContactDetailOverviewBasicInfos />
                    <ScrollToFieldError />
                </>
            )}
        </Formik>
    );
});
